import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { MODULE_TYPES, NODE_COLORS } from '../utils/constants';
import AIModelSelect, { DEFAULT_MODELS, MODEL_FIELD_NAMES } from '../components/common/AIModelSelect';
import { CODE_MODULE, isCodeModule } from '../components/modules/codeModules';

// The code editor (and its syntax highlighter) is only loaded when a code module is opened.
const CodeModuleEditor = lazy(() => import('../components/modules/CodeModuleEditor'));

// A listing entry in the shape CodeModuleEditor expects (a /pipeline-modules/custom row).
const toCustomModule = (mod) => ({ ...mod, id: mod.custom_module_id });

// Fields handled globally or by the AIModelSelect component
const HIDDEN_AI_FIELDS = new Set([
  'api_key', 'openai_api_key', 'anthropic_api_key', 'google_api_key',
  'api_base_url', 'ollama_url', 'local_api_url',
  'provider', 'model', 'ollama_model', 'google_model',
  'openai_model', 'anthropic_model',
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TYPE_LABELS = {
  [MODULE_TYPES.INPUT]: 'Input',
  [MODULE_TYPES.TRANSFORM]: 'Transform',
  [MODULE_TYPES.AI_ENRICHMENT]: 'AI Enrichment',
  [MODULE_TYPES.OUTPUT]: 'Output',
};

/** Tab indices: 0 = All, 1 = Input, 2 = Transform, 3 = AI Enrichment, 4 = Output */
const TAB_TYPES = [null, MODULE_TYPES.INPUT, MODULE_TYPES.TRANSFORM, MODULE_TYPES.AI_ENRICHMENT, MODULE_TYPES.OUTPUT];

const WIZARD_STEPS = ['Select Base', 'Configure Preset', 'Name & Describe', 'Review & Save'];

// Render a single config field based on its type from config_schema
const ConfigField = ({ field, value, onChange }) => {
  const commonProps = {
    fullWidth: true,
    label: field.label || field.name,
    helperText: field.description || '',
    required: !!field.required,
    size: 'small',
    sx: { mb: 2 },
  };

  switch (field.type) {
    case 'string':
      return (
        <TextField
          {...commonProps}
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={!!(value ?? field.default ?? false)}
              onChange={(e) => onChange(field.name, e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                {field.label || field.name}
                {field.required && ' *'}
              </Typography>
              {field.description && (
                <Typography variant="caption" color="text.secondary">
                  {field.description}
                </Typography>
              )}
            </Box>
          }
        />
      );
    case 'select':
      return (
        <FormControl {...commonProps}>
          <InputLabel>{field.label || field.name}</InputLabel>
          <Select
            value={value ?? field.default ?? ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            label={field.label || field.name}
          >
            {(field.options || []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              {field.description}
            </Typography>
          )}
        </FormControl>
      );
    case 'multiline':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={4}
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'json':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={4}
          placeholder='{"key": "value"}'
          value={typeof value === 'string' ? value : (value != null ? JSON.stringify(value, null, 2) : (field.default != null ? JSON.stringify(field.default, null, 2) : ''))}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'array':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={4}
          placeholder='["item1", "item2"]'
          value={typeof value === 'string' ? value : (value != null ? JSON.stringify(value, null, 2) : (field.default != null ? JSON.stringify(field.default, null, 2) : ''))}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    default:
      return (
        <TextField
          {...commonProps}
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
  }
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ModulesPage = () => {
  // Data state
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  // Create wizard state
  const [createOpen, setCreateOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    type: '',
    base_module: '',
    preset_config: {},
    name: '',
    description: '',
    visibility: 'private',
  });
  const [saving, setSaving] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteUsage, setDeleteUsage] = useState(null);

  // Code module editor (null = closed; {module: null} = new)
  const [codeEditor, setCodeEditor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pipeline-modules');
      setModules(response.data.modules || []);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  // /modules?code_module=<id> opens that code module (linked from the pipeline designer)
  const linkedCodeModuleId = searchParams.get('code_module');
  useEffect(() => {
    if (!linkedCodeModuleId) return;
    api.get(`/pipeline-modules/custom/${linkedCodeModuleId}`)
      .then((response) => setCodeEditor({ module: response.data }))
      .catch(() => setError('Code module not found'));
  }, [linkedCodeModuleId]);

  const closeCodeEditor = () => {
    setCodeEditor(null);
    if (linkedCodeModuleId) {
      searchParams.delete('code_module');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // -----------------------------------------------------------------------
  // Derived data
  // -----------------------------------------------------------------------

  const builtinModules = useMemo(() => modules.filter((m) => m.source === 'built_in'), [modules]);

  const filteredModules = useMemo(() => {
    let list = modules;

    // Tab filter
    const typeFilter = TAB_TYPES[selectedTab];
    if (typeFilter) {
      list = list.filter((m) => m.type === typeFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (m) =>
          (m.name || '').toLowerCase().includes(q) ||
          (m.description || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [modules, selectedTab, searchQuery]);

  // Built-in modules of the type selected in wizard step 1
  const baseModuleOptions = useMemo(() => {
    if (!wizardData.type) return [];
    // Code modules are created with the code module editor, not as presets.
    return builtinModules.filter((m) => m.type === wizardData.type && m.id !== CODE_MODULE);
  }, [builtinModules, wizardData.type]);

  // The config_schema of the currently selected base module
  const selectedBaseSchema = useMemo(() => {
    if (!wizardData.base_module) return [];
    const base = builtinModules.find((m) => m.id === wizardData.base_module);
    return base?.config_schema || [];
  }, [builtinModules, wizardData.base_module]);

  // For edit dialog: get the config_schema from the base module of the custom module being edited
  const editBaseSchema = useMemo(() => {
    if (!editData) return [];
    const base = builtinModules.find((m) => m.id === editData.module_name && m.type === editData.type);
    return base?.config_schema || [];
  }, [builtinModules, editData]);

  // -----------------------------------------------------------------------
  // Wizard handlers
  // -----------------------------------------------------------------------

  const openCreateWizard = () => {
    setWizardStep(0);
    setWizardData({ type: '', base_module: '', preset_config: {}, name: '', description: '', visibility: 'private' });
    setCreateOpen(true);
  };

  const closeCreateWizard = () => {
    setCreateOpen(false);
  };

  const handleWizardNext = () => {
    setWizardStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleWizardBack = () => {
    setWizardStep((prev) => Math.max(prev - 1, 0));
  };

  const handlePresetChange = (fieldName, value) => {
    setWizardData((prev) => ({
      ...prev,
      preset_config: { ...prev.preset_config, [fieldName]: value },
    }));
  };

  const canAdvance = () => {
    switch (wizardStep) {
      case 0:
        return wizardData.type && wizardData.base_module;
      case 1:
        return true; // Preset config is optional
      case 2:
        return wizardData.name.trim() !== '';
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Parse JSON/array string values in preset before sending to API
  const preparePresetConfig = (presetConfig, schema) => {
    const result = {};
    for (const [key, val] of Object.entries(presetConfig)) {
      if (val === '' || val === undefined || val === null) continue;
      const field = schema.find((f) => f.name === key);
      if (field && (field.type === 'json' || field.type === 'array') && typeof val === 'string') {
        try {
          result[key] = JSON.parse(val);
        } catch {
          result[key] = val;
        }
      } else {
        result[key] = val;
      }
    }
    return result;
  };

  const handleSaveCustomModule = async () => {
    try {
      setSaving(true);
      const cleanPreset = preparePresetConfig(wizardData.preset_config, selectedBaseSchema);
      await api.post('/pipeline-modules/custom', {
        name: wizardData.name.trim(),
        description: wizardData.description.trim() || null,
        type: wizardData.type,
        base_module: wizardData.base_module,
        preset_config: cleanPreset,
        status: 'active',
        visibility: wizardData.visibility,
      });
      setSuccess('Custom module created successfully');
      closeCreateWizard();
      await fetchModules();
    } catch (err) {
      console.error('Error creating custom module:', err);
      setError(err.response?.data?.detail || 'Failed to create custom module');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Edit handlers
  // -----------------------------------------------------------------------

  const openEditDialog = (mod) => {
    setEditData({
      custom_module_id: mod.custom_module_id,
      module_name: mod.module_name,
      type: mod.type,
      name: mod.name,
      description: mod.description || '',
      preset_config: { ...(mod.preset_config || {}) },
      status: 'active', // default to active for display
      visibility: mod.visibility || 'private',
    });
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditData(null);
  };

  const handleEditPresetChange = (fieldName, value) => {
    setEditData((prev) => ({
      ...prev,
      preset_config: { ...prev.preset_config, [fieldName]: value },
    }));
  };

  const handleUpdateCustomModule = async () => {
    try {
      setSaving(true);
      const cleanPreset = preparePresetConfig(editData.preset_config, editBaseSchema);
      await api.put(`/pipeline-modules/custom/${editData.custom_module_id}`, {
        name: editData.name.trim(),
        description: editData.description.trim() || null,
        preset_config: cleanPreset,
        status: editData.status,
        visibility: editData.visibility,
      });
      setSuccess('Custom module updated successfully');
      closeEditDialog();
      await fetchModules();
    } catch (err) {
      console.error('Error updating custom module:', err);
      setError(err.response?.data?.detail || 'Failed to update custom module');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Delete handlers
  // -----------------------------------------------------------------------

  const openDeleteDialog = (mod) => {
    setDeleteTarget(mod);
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setDeleteTarget(null);
    setDeleteUsage(null);
  };

  const handleDeleteCustomModule = async () => {
    try {
      setSaving(true);
      await api.delete(`/pipeline-modules/custom/${deleteTarget.custom_module_id}`);
      setSuccess('Custom module deleted successfully');
      closeDeleteDialog();
      await fetchModules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.pipelines) {
        setDeleteUsage(detail);  // a code module still used by pipelines: list them
        return;
      }
      console.error('Error deleting custom module:', err);
      setError(typeof detail === 'string' ? detail : 'Failed to delete custom module');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render: Module Card
  // -----------------------------------------------------------------------

  const renderModuleCard = (mod) => {
    const colors = NODE_COLORS[mod.type] || { background: '#f5f5f5', border: '#bdbdbd', text: '#424242' };
    const isCustom = mod.source === 'custom';

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={mod.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: `4px solid ${colors.border}`,
            '&:hover': { boxShadow: 4 },
            transition: 'box-shadow 0.2s',
          }}
        >
          <CardContent sx={{ flexGrow: 1, pb: 1 }}>
            <Typography variant="h6" component="h2" noWrap sx={{ mb: 1 }}>
              {mod.name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip
                label={TYPE_LABELS[mod.type] || mod.type}
                size="small"
                sx={{
                  bgcolor: colors.background,
                  color: colors.text,
                  borderColor: colors.border,
                  fontWeight: 500,
                }}
                variant="outlined"
              />
              {isCodeModule(mod) && <Chip label="Code" size="small" color="secondary" />}
              <Chip
                label={isCustom ? 'Custom' : 'Built-in'}
                size="small"
                color={isCustom ? 'primary' : 'default'}
                variant={isCustom ? 'filled' : 'outlined'}
              />
              {isCustom && (
                <Chip
                  label={mod.visibility === 'public' ? 'Public' : 'Private'}
                  size="small"
                  color={mod.visibility === 'public' ? 'success' : 'default'}
                  variant="outlined"
                />
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: '2.6em',
              }}
            >
              {mod.description || 'No description available.'}
            </Typography>
            {isCustom && mod.author && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                By {mod.author}
              </Typography>
            )}
          </CardContent>

          {isCodeModule(mod) && !mod.can_edit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1 }}>
              <Tooltip title="View code module">
                <IconButton size="small" onClick={() => setCodeEditor({ module: toCustomModule(mod) })}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {isCustom && mod.can_edit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1 }}>
              <Tooltip title={isCodeModule(mod) ? 'Edit code module' : 'Edit custom module'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCodeModule(mod)) setCodeEditor({ module: toCustomModule(mod) });
                    else openEditDialog(mod);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete custom module">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog(mod);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Card>
      </Grid>
    );
  };

  // -----------------------------------------------------------------------
  // Render: Create Wizard Dialog
  // -----------------------------------------------------------------------

  const renderWizardStepContent = () => {
    switch (wizardStep) {
      case 0: // Select Base
        return (
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Module Type</InputLabel>
              <Select
                value={wizardData.type}
                onChange={(e) =>
                  setWizardData((prev) => ({
                    ...prev,
                    type: e.target.value,
                    base_module: '',
                    preset_config: {},
                  }))
                }
                label="Module Type"
              >
                <MenuItem value={MODULE_TYPES.INPUT}>Input</MenuItem>
                <MenuItem value={MODULE_TYPES.TRANSFORM}>Transform</MenuItem>
                <MenuItem value={MODULE_TYPES.AI_ENRICHMENT}>AI Enrichment</MenuItem>
                <MenuItem value={MODULE_TYPES.OUTPUT}>Output</MenuItem>
              </Select>
            </FormControl>

            {wizardData.type && (
              <FormControl fullWidth>
                <InputLabel>Base Module</InputLabel>
                <Select
                  value={wizardData.base_module}
                  onChange={(e) =>
                    setWizardData((prev) => ({
                      ...prev,
                      base_module: e.target.value,
                      preset_config: {},
                    }))
                  }
                  label="Base Module"
                >
                  {baseModuleOptions.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 1: { // Configure Preset
        const isAIModule = selectedBaseSchema.some((f) => f.name === 'provider');
        const visibleFields = selectedBaseSchema.filter((f) => !HIDDEN_AI_FIELDS.has(f.name));
        const currentProvider = wizardData.preset_config.provider || 'openai';
        const currentModelField = MODEL_FIELD_NAMES[currentProvider] || 'model';
        const currentModel = wizardData.preset_config[currentModelField] || DEFAULT_MODELS[currentProvider] || '';

        return (
          <Box sx={{ pt: 1 }}>
            {selectedBaseSchema.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                The selected base module has no configurable fields.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pre-fill configuration values for this custom module. Users can still override these when using the module in a pipeline.
                </Typography>
                {isAIModule && (
                  <Box sx={{ mb: 2 }}>
                    <AIModelSelect
                      provider={currentProvider}
                      model={currentModel}
                      onProviderChange={(newProvider) => {
                        const newPreset = { ...wizardData.preset_config, provider: newProvider };
                        delete newPreset.model;
                        delete newPreset.ollama_model;
                        delete newPreset.google_model;
                        setWizardData((prev) => ({ ...prev, preset_config: newPreset }));
                      }}
                      onModelChange={(newModel) => {
                        setWizardData((prev) => {
                          const provider = prev.preset_config.provider || 'openai';
                          const modelField = MODEL_FIELD_NAMES[provider] || 'model';
                          const newPreset = { ...prev.preset_config, [modelField]: newModel };
                          Object.values(MODEL_FIELD_NAMES).forEach((f) => {
                            if (f !== modelField) delete newPreset[f];
                          });
                          return { ...prev, preset_config: newPreset };
                        });
                      }}
                      size="small"
                      direction="column"
                      showHelperText
                    />
                  </Box>
                )}
                {visibleFields.map((field) => (
                  <ConfigField
                    key={field.name}
                    field={field}
                    value={wizardData.preset_config[field.name]}
                    onChange={handlePresetChange}
                  />
                ))}
              </>
            )}
          </Box>
        );
      }

      case 2: // Name & Describe
        return (
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Module Name"
              required
              value={wizardData.name}
              onChange={(e) => setWizardData((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 3 }}
              placeholder="e.g. S3 EU-West Input"
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={wizardData.description}
              onChange={(e) => setWizardData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this custom module is for..."
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={wizardData.visibility === 'public'}
                  onChange={(e) =>
                    setWizardData((prev) => ({ ...prev, visibility: e.target.checked ? 'public' : 'private' }))
                  }
                />
              }
              label={wizardData.visibility === 'public' ? 'Public (visible to everyone)' : 'Private (only you and admins)'}
            />
          </Box>
        );

      case 3: // Review & Save
        {
          const baseMod = builtinModules.find((m) => m.id === wizardData.base_module);
          return (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Name</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{wizardData.name}</Typography>

              <Typography variant="subtitle2" gutterBottom>Type</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{TYPE_LABELS[wizardData.type] || wizardData.type}</Typography>

              <Typography variant="subtitle2" gutterBottom>Base Module</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{baseMod?.name || wizardData.base_module}</Typography>

              <Typography variant="subtitle2" gutterBottom>Visibility</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {wizardData.visibility === 'public' ? 'Public' : 'Private'}
              </Typography>

              {wizardData.description && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Description</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{wizardData.description}</Typography>
                </>
              )}

              {Object.keys(wizardData.preset_config).length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Preset Configuration</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {Object.entries(wizardData.preset_config).map(([key, val]) => {
                      if (val === '' || val === undefined || val === null) return null;
                      return (
                        <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 140 }}>
                            {key}:
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                </>
              )}
            </Box>
          );
        }

      default:
        return null;
    }
  };

  const renderCreateDialog = () => (
    <Dialog open={createOpen} onClose={closeCreateWizard} maxWidth="md" fullWidth>
      <DialogTitle>Create Custom Module</DialogTitle>
      <DialogContent>
        <Stepper activeStep={wizardStep} sx={{ pt: 1, pb: 3 }}>
          {WIZARD_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderWizardStepContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCreateWizard}>Cancel</Button>
        {wizardStep > 0 && (
          <Button onClick={handleWizardBack}>Back</Button>
        )}
        {wizardStep < WIZARD_STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleWizardNext} disabled={!canAdvance()}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSaveCustomModule} disabled={saving || !canAdvance()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // -----------------------------------------------------------------------
  // Render: Edit Dialog
  // -----------------------------------------------------------------------

  const renderEditDialog = () => {
    if (!editData) return null;

    return (
      <Dialog open={editOpen} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Custom Module</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Module Name"
              required
              value={editData.name}
              onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={editData.description}
              onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  checked={editData.status === 'active'}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, status: e.target.checked ? 'active' : 'draft' }))
                  }
                />
              }
              label={editData.status === 'active' ? 'Active' : 'Draft'}
            />

            <FormControlLabel
              sx={{ mb: 2, display: 'block' }}
              control={
                <Switch
                  checked={editData.visibility === 'public'}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, visibility: e.target.checked ? 'public' : 'private' }))
                  }
                />
              }
              label={editData.visibility === 'public' ? 'Public (visible to everyone)' : 'Private (only you and admins)'}
            />

            {editBaseSchema.length > 0 && (() => {
              const isAIModule = editBaseSchema.some((f) => f.name === 'provider');
              const visibleFields = editBaseSchema.filter((f) => !HIDDEN_AI_FIELDS.has(f.name));
              const currentProvider = editData.preset_config.provider || 'openai';
              const currentModelField = MODEL_FIELD_NAMES[currentProvider] || 'model';
              const currentModel = editData.preset_config[currentModelField] || DEFAULT_MODELS[currentProvider] || '';

              return (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                    Preset Configuration
                  </Typography>
                  {isAIModule && (
                    <Box sx={{ mb: 2 }}>
                      <AIModelSelect
                        provider={currentProvider}
                        model={currentModel}
                        onProviderChange={(newProvider) => {
                          const newPreset = { ...editData.preset_config, provider: newProvider };
                          delete newPreset.model;
                          delete newPreset.ollama_model;
                          delete newPreset.google_model;
                          setEditData((prev) => ({ ...prev, preset_config: newPreset }));
                        }}
                        onModelChange={(newModel) => {
                          setEditData((prev) => {
                            const provider = prev.preset_config.provider || 'openai';
                            const modelField = MODEL_FIELD_NAMES[provider] || 'model';
                            const newPreset = { ...prev.preset_config, [modelField]: newModel };
                            Object.values(MODEL_FIELD_NAMES).forEach((f) => {
                              if (f !== modelField) delete newPreset[f];
                            });
                            return { ...prev, preset_config: newPreset };
                          });
                        }}
                        size="small"
                        direction="column"
                        showHelperText
                      />
                    </Box>
                  )}
                  {visibleFields.map((field) => (
                    <ConfigField
                      key={field.name}
                      field={field}
                      value={editData.preset_config[field.name]}
                      onChange={handleEditPresetChange}
                    />
                  ))}
                </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCustomModule}
            disabled={saving || !editData.name.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // -----------------------------------------------------------------------
  // Render: Delete Confirmation Dialog
  // -----------------------------------------------------------------------

  const renderDeleteDialog = () => (
    <Dialog open={deleteOpen} onClose={closeDeleteDialog}>
      <DialogTitle>Delete Custom Module</DialogTitle>
      <DialogContent>
        {deleteUsage ? (
          <>
            <Typography gutterBottom>
              &quot;{deleteTarget?.name}&quot; can&apos;t be deleted: it is used by these pipelines.
              Remove it from them first.
            </Typography>
            <Box component="ul" sx={{ my: 1, pl: 3 }}>
              {deleteUsage.pipelines.map((p) => (
                <li key={p.id}>
                  <RouterLink to={`/pipelines/${p.id}`}>{p.name}</RouterLink>
                </li>
              ))}
              {deleteUsage.hidden > 0 && (
                <li>
                  {deleteUsage.hidden} pipeline{deleteUsage.hidden === 1 ? '' : 's'} you can&apos;t see
                </li>
              )}
            </Box>
          </>
        ) : (
          <Typography>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This action cannot be undone.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDeleteDialog}>{deleteUsage ? 'Close' : 'Cancel'}</Button>
        {!deleteUsage && (
          <Button onClick={handleDeleteCustomModule} color="error" variant="contained" disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // -----------------------------------------------------------------------
  // Main Render
  // -----------------------------------------------------------------------

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Modules
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<CodeIcon />} onClick={() => setCodeEditor({ module: null })}>
            New code module
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateWizard}>
            Create Custom Module
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search modules..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" />
          <Tab label="Input" />
          <Tab label="Transform" />
          <Tab label="AI Enrichment" />
          <Tab label="Output" />
        </Tabs>
      </Paper>

      {/* Module Grid */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredModules.map(renderModuleCard)}
            {filteredModules.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No modules found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? 'Try adjusting your search terms.'
                      : 'Get started by creating your first custom module.'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Dialogs */}
      {renderCreateDialog()}
      {renderEditDialog()}
      {renderDeleteDialog()}
      {codeEditor && (
        <Suspense fallback={null}>
          <CodeModuleEditor open module={codeEditor.module} onClose={closeCodeEditor} onSaved={fetchModules} />
        </Suspense>
      )}

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModulesPage;
